import type { DicomInfo } from '@/lib/dicom/DicomInfo';
import { PatientComparisonUtil } from '@/lib/dicom/PatientComparisonUtil';
import type { PatientPersonalInfo } from '@/lib/dicom/types';

/** Cap on individually-named conflicting files before collapsing to "N other files". */
const FILES_TO_DISPLAY = 10;

export interface PatientIdentityGroup {
    patient: PatientPersonalInfo | null;
    fileCount: number;
}

export interface PatientIdentityConflict {
    /** One row per distinct patient identity found across the uploaded files. */
    groups: PatientIdentityGroup[];
    /** Names of the files belonging to the smaller (assumed-incorrect) identities. */
    conflictFileNames: string[];
    /**
     * Human-readable list of the conflicting file names, truncated to the first
     * {@link FILES_TO_DISPLAY} with an "N other files" tail.
     */
    formattedConflictList: string;
}

function toGroup(dicoms: DicomInfo[]): PatientIdentityGroup {
    return {
        patient: dicoms.length ? PatientComparisonUtil.fromDicom(dicoms[0]) : null,
        fileCount: dicoms.length,
    };
}

/**
 * The largest identity group is assumed correct; every other group holds files
 * whose patient details are inconsistent with it.
 */
function conflictingDicoms(identities: DicomInfo[][]): DicomInfo[] {
    const byCountDesc = [...identities].sort((a, b) => b.length - a.length);
    byCountDesc.shift();
    return byCountDesc.flat();
}

function formatConflictList(fileNames: string[]): string {
    const formatter = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' });
    const shown = fileNames.slice(0, FILES_TO_DISPLAY);
    const remaining = fileNames.length - shown.length;
    if (remaining > 0) {
        const tail = `${remaining} other ${remaining === 1 ? 'file' : 'files'}`;
        return formatter.format([...shown, tail]);
    }
    return formatter.format(shown);
}

/**
 * Summarise an inconsistent-patient-identity check into the display data the
 * modal renders: a row per identity, plus the conflicting file names and their
 * formatted list.
 */
export function summarisePatientIdentityConflict(
    identities: DicomInfo[][],
): PatientIdentityConflict {
    const conflictFileNames = conflictingDicoms(identities).map((d) => d.file.name);
    return {
        groups: identities.map(toGroup),
        conflictFileNames,
        formattedConflictList: formatConflictList(conflictFileNames),
    };
}
